import React from "react"
import { Linking, View } from "react-native"
import { Vertical } from "@/components/standard/BFLayout"
import { MilestoneItem } from "./MilestoneItem"
import type { MilestoneType } from "@/generated/graphql"
import { useQuery } from "@tanstack/react-query"
import {
  loanDashboardQueryOptions,
  useSkipLoanProcessorKickoffCall,
} from "@/queries/loan-dashboard"
import { router } from "expo-router"
import { loanAppLoanWizard } from "@/screens/loans/utils/LoanUtils"
import { ProcessorCard } from "@/screens/loans/components/dashboard/milestones/ProcessorCard"
import sdk from "@/api/sdk"
import { ActionCard } from "@/screens/loans/components/dashboard/milestones/ActionCard"

import { showErrorToast, showSuccessToast } from "@/components/toasts/AppToast"

interface MilestoneListProps {
  loanId: string
}

const isMilestoneActive = (milestone: MilestoneType) =>
  milestone.status !== "completed" && milestone.status !== "blocked"

export const MilestoneList: React.FC<MilestoneListProps> = ({ loanId }) => {
  const { data: loanApp } = useQuery({
    ...loanDashboardQueryOptions(loanId),
    select: (data) => {
      return data!
    },
  })

  const optOutMutation = useSkipLoanProcessorKickoffCall()

  const milestones = loanApp?.milestones
  const processing = loanApp?.processing

  if (!milestones) {
    return null
  }

  // Special actions based on milestone type and status
  const getSpecialActions = (
    milestone: MilestoneType
  ): (() => JSX.Element) | undefined => {
    if (!isMilestoneActive(milestone)) {
      return undefined
    }

    if (milestone.name?.toLowerCase().includes("quote")) {
      return () => (
        <ActionCard
          title={
            processing?.termSheetUrl
              ? "View Term Sheet"
              : milestone.status === "completed"
                ? "View Quote"
                : "Continue Quote"
          }
          testID={"view-quote-action"}
          onPress={() => {
            if (processing?.termSheetUrl) {
              void (async () => {
                const fileData = await sdk.getHubspotFileUrl({
                  fileId: processing.termSheetUrl!,
                })
                if (fileData?.getHubspotFileUrl?.fileUrl?.url) {
                  void Linking.openURL(
                    fileData?.getHubspotFileUrl?.fileUrl?.url
                  )
                } else {
                  alert("Cannot open file")
                }
              })()
            } else {
              router.push(
                milestone.status === "completed"
                  ? `/loans/${loanId}/dashboard/details`
                  : loanAppLoanWizard(loanId)
              )
            }
          }}
        />
      )
    }

    function optoutPress() {
      optOutMutation.mutate(
        { loanId: loanApp!.id },
        {
          onSuccess: () => {
            showSuccessToast({
              title: "Success",
              message:
                "You have opted out of the kickoff call. Please give it a few minutes to update the status.",
            })
          },
          onError: () => {
            showErrorToast({
              title: "Error",
              message:
                "Failed to update status. Please try again in a few minutes.",
            })
          },
        }
      )
    }

    if (milestone?.id === "meet_your_processor" && processing?.loanProcessor) {
      console.log("Adding loan card")
      return () => (
        <ProcessorCard
          optoutPress={optoutPress}
          loanProcessor={processing.loanProcessor!}
          callDate={
            processing?.processingKickoffCallDate
              ? new Date(processing.processingKickoffCallDate)
              : undefined
          }
        />
      )
    }

    const hasTasks = milestone.tasks && milestone.tasks.length > 0

    if (
      hasTasks &&
      milestone.status !== "completed" &&
      milestone.status !== "blocked"
    ) {
      console.log(milestone.tasks)
      return () => (
        <ActionCard
          testID={"view-requests"}
          title={"View Requests"}
          onPress={() => {
            router.push("./requests")
          }}
        />
      )
    }

    return undefined
  }

  return (
    <Vertical className={`gap-1`}>
      <View className="bg-card border border-border rounded p-5">
        {milestones.map((milestone, index) => (
          <MilestoneItem
            key={milestone.id}
            milestone={milestone}
            isFirstBlocked={
              milestone.status === "blocked" &&
              (index === 0 || milestones[index - 1].status !== "blocked")
            }
            isLast={index === milestones.length - 1}
            renderSpecialContent={getSpecialActions(milestone)}
          />
        ))}
      </View>
    </Vertical>
  )
}
